import React, { useRef, useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { GlobalSideNavigation, type ItemsConfig } from '@atlassian/global-side-navigation';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import type {
	GSNForContentSidebarQuery,
	GSNForContentSidebarQuery$data,
} from '@atlassian/jira-relay/src/__generated__/GSNForContentSidebarQuery.graphql';
import { SidebarNav4ContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { Nav4Filters } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-filters/src/index.tsx';
import {
	L1_MENU_ID,
	USER_NAVIGATION_ITEM_LINKS,
	type JiraConfigurableNavigationItem,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4Apps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { CustomerServiceAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-customer-service-analytics/src/index.tsx';
import { CustomerService } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Nav4Dashboards } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Operations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Communications } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-communications/src/index.tsx';
import { Nav4Overviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/index.tsx';
import { Nav4Plans } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Recent } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/index.tsx';
import { Nav4Roadmaps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Starred } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/index.tsx';
import { Teams } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useShouldRenderOverviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/controllers/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import { USER_NAVIGATION_CONFIGURATION_DEFAULT } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { Stub } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/stub/main.tsx';
import { navPlatformSidebarResource } from '@atlassian/jira-nav-platform-integration-resources/src/services/sidebar/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { isTeamsAppEnabled } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { getForYouConfig } from '../common/ui/getForYouConfig.tsx';

const forYouConfig = getForYouConfig();

export function GSNForContentSidebar() {
	/*
		TODO: Add support for entryPoint routes

	*/
	// This is the non-EP solution for loading the data
	const { queryReference } = useRelayResource<GSNForContentSidebarQuery>(
		navPlatformSidebarResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GSNForContentSidebarQuery> | null>(null);

	// We don't want to use nullable query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	} else if (!queryReferenceRef.current) {
		return null;
	}

	return <GSNForContentSidebarInner queryReference={queryReferenceRef.current} />;
}

type Props = {
	queryReference: PreloadedQuery<GSNForContentSidebarQuery>;
};

function GSNForContentSidebarInner(props: Props) {
	const { queryReference } = props;

	const queryRef = usePreloadedQuery<GSNForContentSidebarQuery>(
		// Need to disable this rule for the useNetworkSideNavUserData fragment (which is inside GSN)
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		graphql`
			query GSNForContentSidebarQuery(
				$cloudId: ID!
				$entityAriProjects: ID!
				$entityAriUserSideNavSettings: ID!
				$isAppsExpanded: Boolean = false
				$isFiltersExpanded: Boolean = false
				$isPlansExpanded: Boolean = false
				$isProjectsExpanded: Boolean = false
				$isOperationsExpanded: Boolean = false
				$isOverviewsExpanded: Boolean = false
				$ownerAri: ID
			) {
				...useNetworkSideNavUserData
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Overviews
				...Nav4Plans
				...Nav4Projects
			}
		`,
		queryReference,
	);
	const shouldRenderOverviews = useShouldRenderOverviews();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const ownerId = useAccountId();

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
	);

	const items = useMemo(() => {
		return buildItemConfig({
			navItems,
			shouldRenderOverviews,
			componentProps: { queryRef, isExpanded: false },
		});
	}, [navItems, shouldRenderOverviews, queryRef]);

	const networkStorageParams = useMemo(() => {
		const entityAri = JiraWorkspaceAri.create({ siteId: cloudId, activationId }).toString();
		const ownerAri = (ownerId && IdentityUserAri.create({ userId: ownerId }).toString()) || '';
		return { entityAri, ownerAri, queryRef };
	}, [cloudId, activationId, ownerId, queryRef]);

	return (
		<SidebarNav4ContextProvider>
			<GlobalSideNavigation
				app="jira"
				appLabel="Jira"
				customizationStore="network"
				forYou={forYouConfig}
				items={items}
				networkStorageParams={networkStorageParams}
				testId="nav-platform-integration-sidebar.ui.global-side-navigation"
			/>
		</SidebarNav4ContextProvider>
	);
}

type ComponentProps = {
	queryRef: GSNForContentSidebarQuery$data;
	isExpanded: boolean;
};

type BuildItemConfigParams = {
	componentProps: ComponentProps;
	navItems: JiraConfigurableNavigationItem[];
	shouldRenderOverviews: boolean;
};

function buildItemConfig(params: BuildItemConfigParams) {
	const { navItems, componentProps, shouldRenderOverviews } = params;

	const components = navItems.map(({ menuId }) => {
		switch (menuId) {
			case L1_MENU_ID.APPS:
				return <Nav4Apps {...componentProps} />;
			case L1_MENU_ID.ASSETS:
				/* remove this case on
				 * assets_as_an_app_v2 cleanup
				 */
				return <Nav4Assets isExpanded={componentProps.isExpanded} />;
			case L1_MENU_ID.DASHBOARDS:
				return <Nav4Dashboards isExpanded={componentProps.isExpanded} />;
			case L1_MENU_ID.FILTERS:
				return <Nav4Filters {...componentProps} />;
			case L1_MENU_ID.GOALS:
				return (
					<Stub menuId={L1_MENU_ID.GOALS} href={USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS]} />
				);
			case L1_MENU_ID.OPERATIONS:
				return <Nav4Operations {...componentProps} />;
			case L1_MENU_ID.COMMUNICATION:
				return <Nav4Communications isExpanded={componentProps.isExpanded} />;
			case L1_MENU_ID.PLANS:
				return <Nav4Plans {...componentProps} />;
			case L1_MENU_ID.OVERVIEWS:
				return shouldRenderOverviews && fg('blu-2858-sidebar-menu-overviews') ? (
					<Nav4Overviews {...componentProps} />
				) : null;
			case L1_MENU_ID.PROJECTS:
				return <Nav4Projects {...componentProps} />;
			case L1_MENU_ID.RECENT:
				return <Recent />;
			case L1_MENU_ID.ROADMAPS:
				return <Nav4Roadmaps isExpanded={componentProps.isExpanded} />;
			case L1_MENU_ID.STARRED:
				return <Starred />;
			case L1_MENU_ID.TEAMS:
				return !isTeamsAppEnabled() ? <Teams /> : null;
			case L1_MENU_ID.CUSTOMER_SERVICE:
				return fg('jcs_master_flag') ? <CustomerService /> : null;
			case L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS:
				return fg('jcs-analytics-page') ? <CustomerServiceAnalytics /> : null;
			default:
				return null;
		}
	});

	return navItems.reduce<ItemsConfig>((acc, navItem, index) => {
		acc[navItem.menuId] = { type: 'custom', Component: () => components[index] };
		return acc;
	}, {});
}
